import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Rocket, Eye, Loader2 } from 'lucide-react';
import { useLang, tx } from '../../i18n/LanguageContext';
import { stripHtml } from '../../lib/richText';
import { EditorCard } from './ui';

export const ReviewStep = ({ form, lessons, onPublish, busy }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const checks = [
    [L('اسم الكورس بالعربية والإنجليزية', 'Course name in both languages'), form.titleAr && form.titleEn],
    [L('وصف الكورس', 'Course description'), stripHtml(form.descriptionAr).trim()],
    [L('صورة الكورس', 'Course image'), form.cover || form.imagePreview],
    [L('نقطة واحدة على الأقل في «ماذا سيتعلم الطالب»', 'At least one learning point'), form.learnAr.some(x => x.trim())],
    [L('درس واحد على الأقل', 'At least one lesson'), lessons.length > 0],
  ];
  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-6">
      <EditorCard testId="course-review-checklist" Icon={CheckCircle2} title={L('مراجعة قبل النشر', 'Review before publishing')} desc={L('تأكد من اكتمال معلومات الكورس', 'Make sure the course is complete')}>
        <ul className="space-y-3">
          {checks.map(([label, ok], i) => (
            <li key={i} data-testid={`review-check-${i}`} className="flex items-center gap-3 text-sm">
              {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300" />}
              <span className={ok ? 'text-slate-800' : 'text-slate-400'}>{label}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2 mt-6">
          <button type="button" data-testid="course-publish-btn" disabled={busy} onClick={onPublish}
            className="h-12 px-7 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold inline-flex items-center gap-2 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}{L('نشر الكورس', 'Publish course')}
          </button>
          <Link to={`/courses/${form.id}`} target="_blank" data-testid="course-view-btn" className="h-12 px-6 rounded-xl border border-slate-200 text-slate-700 font-bold inline-flex items-center gap-2 hover:bg-slate-50 transition-colors">
            <Eye className="w-4 h-4" />{L('معاينة صفحة الكورس', 'Preview course page')}
          </Link>
        </div>
      </EditorCard>
      <div data-testid="course-review-preview" className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm self-start">
        <div className="aspect-video bg-slate-100">{(form.imagePreview || form.cover) && <img src={form.imagePreview || form.cover} alt="" className="w-full h-full object-cover" />}</div>
        <div className="p-5">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {form.featured && <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-xs font-bold">{L('مميز', 'Featured')}</span>}
            {form.is_free && <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-xs font-bold">{L('مجاني', 'Free')}</span>}
            {!form.published && <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-bold">{L('مسودة', 'Draft')}</span>}
          </div>
          <h3 className="font-extrabold text-slate-900 text-lg">{lang === 'ar' ? form.titleAr : form.titleEn}</h3>
          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{lang === 'ar' ? form.subtitleAr : form.subtitleEn}</p>
          <div className="text-xs text-slate-500 mt-3">{form.hours || 0} {L('ساعة', 'hours')} • {lessons.length} {L('درس', 'lessons')} • {lang === 'ar' ? form.levelAr : form.levelEn}</div>
        </div>
      </div>
    </div>
  );
};
